import { type IAdminAuthRequestContext } from '@mahawi/eshop-common';
import { type Account, type Transaction } from '@mahawi/eshop-database';

export enum ESocketActions {
  SHOW_NOTIFICATION = 'showNotification',
  EVENT_GENERAL = 'event.general',
}

export enum EMessengerSocketEvent {
  MESSAGE = 'messenger.message',
  EVENT = 'event.message',
  LEAD_SESSIONS = 'MESSENGER_LEAD_SESSIONS',
  LEAD_BASKET_PRODUCTS = 'MESSENGER_LEAD_BASKET_PRODUCTS',
  LEAD_ACTIVITY = 'MESSENGER_LEAD_ACTIVITY',
}

export enum ESocketRoom {
  MESSENGER = 'messenger',
  MESSENGER_LEAD_PREFIX = 'messenger/lead/',
}

export const getMessengerLeadRoom = (leadUUID: string): string =>
  `${ESocketRoom.MESSENGER_LEAD_PREFIX}${leadUUID}`;

export enum ENotificationSeverity {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  SUCCESS = 'success',
}

export interface ShowNotificationMessage {
  severity: ENotificationSeverity;
  title: React.ReactNode;
  description: React.ReactNode;
}

export type ShowNotificationCallback = (
  message: ShowNotificationMessage,
) => void;

export type SocketWrappedArguments<MESSAGE, CALLBACK> = {
  account: Account;
  message: MESSAGE;
  cb: CALLBACK;
  transaction: Transaction;
  showNotificationCallback: ShowNotificationCallback;
  requestContext?: IAdminAuthRequestContext;
  sessionUuid?: string;
};

export type SocketWrappedFunction<MESSAGE, CALLBACK> = ({
  account,
  message,
  cb,
  transaction,
  showNotificationCallback,
}: SocketWrappedArguments<MESSAGE, CALLBACK>) => Promise<void>;

export interface SocketListeners {
  [key: string]: SocketWrappedFunction<unknown, unknown>;
}

export type SocketWrappedArgumentsWithoutTransaction<MESSAGE, CALLBACK> = Omit<
  SocketWrappedArguments<MESSAGE, CALLBACK>,
  'transaction'
>;

export type SocketWrappedFunctionWithoutTransaction<MESSAGE, CALLBACK> = (
  args: SocketWrappedArgumentsWithoutTransaction<MESSAGE, CALLBACK>,
) => Promise<void>;

export interface TransactionlessSocketListeners {
  [key: string]: SocketWrappedFunctionWithoutTransaction<unknown, unknown>;
}

export type SocketCallback = (response: object) => void;

export enum ERequestFailedReason {
  SEARCH_UNAVAILABLE = 'searchUnavailable',
  INTERNAL_ERROR = 'internalError',
  UNAUTHORIZED = 'unauthorized',
}

export enum ERequestFailedActionType {
  REQUEST_FAILED = 'Socket/requestFailed',
}

export interface IRequestFailedPayload {
  requestType: string;
  reason: ERequestFailedReason;
}

export interface RequestFailedAction {
  type: ERequestFailedActionType.REQUEST_FAILED;
  payload: IRequestFailedPayload;
}
